import * as THREE from "three";
import type { RacePresentationSnapshot } from "../render/types";
import {
  createImpactTriggerGate,
  finiteAudioValue,
  normalizedAudioValue
} from "./signals";

export type SuntraceAudioControllerOptions = {
  context?: AudioContext;
  volume?: number;
  muted?: boolean;
};

export type SuntraceAudioController = {
  unlock: () => Promise<void>;
  update: (snapshot: RacePresentationSnapshot, deltaSeconds: number) => void;
  setMuted: (muted: boolean) => void;
  setVolume: (volume: number) => void;
  dispose: () => void;
};

export const getSuntraceAudioPhaseGain = (
  phase: RacePresentationSnapshot["phase"],
): number => {
  if (phase === "racing" || phase === "countdown") return 1;
  if (phase === "paused") return 0.24;
  return 0;
};

const createNoiseBuffer = (
  context: AudioContext,
  duration: number,
  seed: number,
): AudioBuffer => {
  const frameCount = Math.ceil(context.sampleRate * duration);
  const buffer = context.createBuffer(1, frameCount, context.sampleRate);
  const channel = buffer.getChannelData(0);
  let state = seed >>> 0;
  let previous = 0;
  for (let index = 0; index < frameCount; index += 1) {
    state = (state * 1664525 + 1013904223) >>> 0;
    const white = state / 4_294_967_296 * 2 - 1;
    previous = previous * 0.78 + white * 0.22;
    channel[index] = previous;
  }
  return buffer;
};

const setSmooth = (
  parameter: AudioParam,
  value: number,
  time: number,
  response = 0.035,
): void => {
  parameter.cancelScheduledValues(time);
  parameter.setTargetAtTime(value, time, response);
};

export const createSuntraceAudioController = (
  options: SuntraceAudioControllerOptions = {},
): SuntraceAudioController => {
  const ownsContext = options.context === undefined;
  const context = options.context ?? new AudioContext({ latencyHint: "interactive" });
  const master = context.createGain();
  const compressor = context.createDynamicsCompressor();
  const enginePan = context.createStereoPanner();
  const engineFilter = context.createBiquadFilter();
  const engineGain = context.createGain();
  const engineFundamental = context.createOscillator();
  const engineHarmonic = context.createOscillator();
  const harmonicGain = context.createGain();
  const windSource = context.createBufferSource();
  const windFilter = context.createBiquadFilter();
  const windGain = context.createGain();
  const tireSource = context.createBufferSource();
  const tireFilter = context.createBiquadFilter();
  const tireGain = context.createGain();
  const boostOscillator = context.createOscillator();
  const boostFilter = context.createBiquadFilter();
  const boostGain = context.createGain();
  const impactBuffer = createNoiseBuffer(context, 0.34, 0x291f47a3);

  master.gain.value = options.muted === true ? 0 : normalizedAudioValue(options.volume ?? 0.68);
  compressor.threshold.value = -12;
  compressor.knee.value = 8;
  compressor.ratio.value = 4;
  compressor.attack.value = 0.008;
  compressor.release.value = 0.16;
  master.connect(compressor).connect(context.destination);

  engineFilter.type = "lowpass";
  engineFilter.frequency.value = 1_800;
  engineFilter.Q.value = 0.9;
  engineGain.gain.value = 0;
  enginePan.pan.value = 0;
  engineFundamental.type = "sawtooth";
  engineHarmonic.type = "triangle";
  harmonicGain.gain.value = 0.34;
  engineFundamental.connect(engineFilter);
  engineHarmonic.connect(harmonicGain).connect(engineFilter);
  engineFilter.connect(engineGain).connect(enginePan).connect(master);

  windSource.buffer = createNoiseBuffer(context, 2.4, 0x55ccee11);
  windSource.loop = true;
  windFilter.type = "bandpass";
  windFilter.frequency.value = 900;
  windFilter.Q.value = 0.46;
  windGain.gain.value = 0;
  windSource.connect(windFilter).connect(windGain).connect(master);

  tireSource.buffer = createNoiseBuffer(context, 1.7, 0x72aae913);
  tireSource.loop = true;
  tireFilter.type = "highpass";
  tireFilter.frequency.value = 1_500;
  tireFilter.Q.value = 0.72;
  tireGain.gain.value = 0;
  tireSource.connect(tireFilter).connect(tireGain).connect(master);

  boostOscillator.type = "sine";
  boostOscillator.frequency.value = 480;
  boostFilter.type = "bandpass";
  boostFilter.frequency.value = 1_500;
  boostFilter.Q.value = 1.8;
  boostGain.gain.value = 0;
  boostOscillator.connect(boostFilter).connect(boostGain).connect(master);

  engineFundamental.start();
  engineHarmonic.start();
  windSource.start();
  tireSource.start();
  boostOscillator.start();

  let muted = options.muted ?? false;
  let volume = normalizedAudioValue(options.volume ?? 0.68);
  const sampleImpactTrigger = createImpactTriggerGate();
  let disposed = false;

  const triggerImpact = (strength: number): void => {
    if (context.state === "closed" || strength < 0.08) {
      return;
    }
    const now = context.currentTime;
    const impactSource = context.createBufferSource();
    const impactFilter = context.createBiquadFilter();
    const impactGain = context.createGain();
    impactSource.buffer = impactBuffer;
    impactFilter.type = "lowpass";
    impactFilter.frequency.setValueAtTime(1_200 + strength * 1_600, now);
    impactFilter.frequency.exponentialRampToValueAtTime(120, now + 0.34);
    impactGain.gain.setValueAtTime(0.0001, now);
    impactGain.gain.exponentialRampToValueAtTime(0.34 * strength, now + 0.008);
    impactGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    impactSource.connect(impactFilter).connect(impactGain).connect(master);
    impactSource.addEventListener("ended", () => {
      impactSource.disconnect();
      impactFilter.disconnect();
      impactGain.disconnect();
    });
    impactSource.start(now);
    impactSource.stop(now + 0.35);
  };

  return {
    async unlock() {
      if (!disposed && context.state === "suspended") {
        await context.resume();
      }
    },
    update(snapshot) {
      if (disposed || context.state === "closed") {
        return;
      }
      const now = context.currentTime;
      const speed = Math.max(0, finiteAudioValue(snapshot.speed));
      const speedFactor = THREE.MathUtils.clamp(speed / 88, 0, 1);
      const steering = THREE.MathUtils.clamp(finiteAudioValue(snapshot.steering), -1, 1);
      const signedSlip = THREE.MathUtils.clamp(finiteAudioValue(snapshot.slip), -1, 1);
      const slip = Math.abs(signedSlip);
      const drift = normalizedAudioValue(snapshot.drift);
      const boost = normalizedAudioValue(snapshot.boost);
      const time = Math.max(0, finiteAudioValue(snapshot.time));
      const phaseGain = getSuntraceAudioPhaseGain(snapshot.phase);
      const syntheticGear = Math.min(6, 1 + Math.floor(speed / 14.5));
      const gearSpan = 14.5;
      const gearProgress = (speed % gearSpan) / gearSpan;
      const rpm = 72 + syntheticGear * 11 + gearProgress * 96 + boost * 34;
      setSmooth(engineFundamental.frequency, rpm, now, 0.025);
      setSmooth(engineHarmonic.frequency, rpm * 2.02, now, 0.025);
      setSmooth(engineFilter.frequency, 1_100 + speedFactor * 4_400 + boost * 1_800, now, 0.045);
      setSmooth(engineGain.gain, (0.075 + speedFactor * 0.15) * phaseGain, now, 0.05);
      setSmooth(enginePan.pan, THREE.MathUtils.clamp(steering * 0.12 + signedSlip * 0.16, -0.3, 0.3), now, 0.07);
      setSmooth(windFilter.frequency, 500 + speedFactor * 2_900, now, 0.09);
      setSmooth(windGain.gain, Math.pow(speedFactor, 1.7) * 0.13 * phaseGain, now, 0.11);
      setSmooth(tireFilter.frequency, 1_100 + speedFactor * 2_400, now, 0.06);
      setSmooth(
        tireGain.gain,
        (slip * 0.15 + drift * 0.12) * (0.4 + speedFactor * 0.6) * phaseGain,
        now,
        0.035,
      );
      setSmooth(boostOscillator.frequency, 410 + boost * 690 + Math.sin(time * 12) * 26, now, 0.04);
      setSmooth(boostFilter.frequency, 1_000 + boost * 3_700, now, 0.05);
      setSmooth(boostGain.gain, boost * 0.095 * phaseGain, now, 0.025);
      const { impact, trigger } = sampleImpactTrigger(snapshot.impact);
      if (trigger && snapshot.phase !== "finished") {
        triggerImpact(impact);
      }
    },
    setMuted(nextMuted) {
      muted = nextMuted;
      if (context.state !== "closed") {
        setSmooth(master.gain, muted ? 0 : volume, context.currentTime, 0.025);
      }
    },
    setVolume(nextVolume) {
      volume = normalizedAudioValue(nextVolume);
      if (!muted && context.state !== "closed") {
        setSmooth(master.gain, volume, context.currentTime, 0.035);
      }
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      engineFundamental.stop();
      engineHarmonic.stop();
      windSource.stop();
      tireSource.stop();
      boostOscillator.stop();
      engineFundamental.disconnect();
      engineHarmonic.disconnect();
      harmonicGain.disconnect();
      engineFilter.disconnect();
      engineGain.disconnect();
      enginePan.disconnect();
      windSource.disconnect();
      windFilter.disconnect();
      windGain.disconnect();
      tireSource.disconnect();
      tireFilter.disconnect();
      tireGain.disconnect();
      boostOscillator.disconnect();
      boostFilter.disconnect();
      boostGain.disconnect();
      master.disconnect();
      compressor.disconnect();
      if (ownsContext && context.state !== "closed") {
        void context.close();
      }
    },
  };
};
